/**
 * Definições e constantes de módulos granulares contratáveis do CRM.
 */

export interface SystemModuleDefinition {
  id: string;
  name: string;
  description: string;
  category: 'workspace' | 'crm' | 'operations' | 'ai' | 'finance' | 'integrations' | 'admin';
}

export const MODULE_CATEGORIES: { id: string; name: string; iconName: string }[] = [
  { id: 'workspace', name: 'Atendimento & Mensageria', iconName: 'MessageSquare' },
  { id: 'crm', name: 'CRM & Comercial', iconName: 'Building2' },
  { id: 'operations', name: 'Operações & Produtividade', iconName: 'CheckSquare' },
  { id: 'ai', name: 'Inteligência Artificial & Automação', iconName: 'Bot' },
  { id: 'finance', name: 'Financeiro & Governança', iconName: 'CreditCard' },
  { id: 'integrations', name: 'Integrações & Extensões', iconName: 'Link' },
];

export const SYSTEM_MODULES: SystemModuleDefinition[] = [
  // 1. Atendimento & Mensageria
  {
    id: 'inbox_conversations',
    name: 'Inbox & Conversas Multicanal',
    description: 'Central de conversas, chat e atendimento ao cliente em tempo real.',
    category: 'workspace'
  },
  {
    id: 'inbox_channels',
    name: 'Canais de Atendimento',
    description: 'Conexão e gestão de canais WhatsApp, Instagram Direct e Meta.',
    category: 'workspace'
  },
  {
    id: 'inbox_social_ops',
    name: 'Oportunidades Sociais',
    description: 'Social listening, radar de engajamento e comentários em posts.',
    category: 'workspace'
  },
  {
    id: 'inbox_social_health',
    name: 'Saúde Social & SLAs',
    description: 'Métricas de SLA, tempo de resposta e qualidade do atendimento social.',
    category: 'workspace'
  },

  // 2. CRM & Comercial
  {
    id: 'crm_leads',
    name: 'Gestão de Leads',
    description: 'Cadastro, segmentação, qualificação e histórico completo de leads.',
    category: 'crm'
  },
  {
    id: 'crm_deals',
    name: 'Oportunidades & Negócios',
    description: 'Acompanhamento de negócios abertos, valores e probabilidades de fechamento.',
    category: 'crm'
  },
  {
    id: 'crm_pipelines',
    name: 'Pipelines & Funis Kanban',
    description: 'Gestão de múltiplos estágios de vendas em visualização Kanban.',
    category: 'crm'
  },
  {
    id: 'crm_contacts',
    name: 'Diretório de Contatos',
    description: 'Gestão e histórico de pessoas de contato e tomadores de decisão.',
    category: 'crm'
  },
  {
    id: 'crm_companies',
    name: 'Contas & Empresas',
    description: 'Cadastro institucional de empresas parceiras e clientes corporativos.',
    category: 'crm'
  },
  {
    id: 'crm_lead_sources',
    name: 'Fontes de Tráfego & UTMs',
    description: 'Rastreamento de origens, canais de aquisição e campanhas de entrada.',
    category: 'crm'
  },
  {
    id: 'crm_tags',
    name: 'Tags de Segmentação',
    description: 'Marcação rápida e segmentação inteligente de contatos e leads.',
    category: 'crm'
  },
  {
    id: 'crm_custom_fields',
    name: 'Campos Customizados',
    description: 'Criação de propriedades e campos adicionais para entidades do CRM.',
    category: 'crm'
  },

  // 3. Operações & Produtividade
  {
    id: 'ops_tasks',
    name: 'Gestão de Tarefas',
    description: 'Listas de tarefas, checklists e prazos de execução para o time.',
    category: 'operations'
  },
  {
    id: 'ops_schedules',
    name: 'Agendamentos & Calendário',
    description: 'Agenda de reuniões, compromissos e integração de horários.',
    category: 'operations'
  },
  {
    id: 'ops_incidents',
    name: 'Central de Incidentes',
    description: 'Monitoramento e resolução de anomalias operacionais e alertas críticos.',
    category: 'operations'
  },

  // 4. Inteligência Artificial & Automação
  {
    id: 'ai_agents',
    name: 'Agentes Autônomos de IA',
    description: 'Configuração e execução de agentes de atendimento e vendas (SDR, CS).',
    category: 'ai'
  },
  {
    id: 'ai_knowledge_base',
    name: 'Base de Conhecimento RAG',
    description: 'Documentos e diretrizes corporativas usadas pelos modelos de IA.',
    category: 'ai'
  },
  {
    id: 'ai_automations',
    name: 'Regras de Automação',
    description: 'Gatilhos automáticos de eventos, atribuição e ações sem código.',
    category: 'ai'
  },
  {
    id: 'ai_flow_builder',
    name: 'Flow Builder Visual',
    description: 'Construtor visual de fluxos de conversação e árvores de decisão.',
    category: 'ai'
  },
  {
    id: 'ai_campaigns',
    name: 'Campanhas & Disparos',
    description: 'Gestão de campanhas promocionais, recuperação e marketing multicanal.',
    category: 'ai'
  },
  {
    id: 'ai_social_content',
    name: 'Conteúdo Social & Criativos',
    description: 'Geração com IA e agendamento de posts para Instagram e redes sociais.',
    category: 'ai'
  },
  {
    id: 'ai_lead_discovery',
    name: 'Prospecção B2B Ativa (Discovery)',
    description: 'Busca ativa de empresas e leads no Google Places e fontes abertas.',
    category: 'ai'
  },
  {
    id: 'ai_growth_engine',
    name: 'Growth Engine Autônomo',
    description: 'Otimização autônoma de criativos, orçamentos e campanhas de tráfego.',
    category: 'ai'
  },

  // 5. Financeiro & Governança
  {
    id: 'fin_invoicing',
    name: 'Faturamento & Cobranças',
    description: 'Geração de PIX, boletos e transações integradas de venda.',
    category: 'finance'
  },
  {
    id: 'fin_subscriptions',
    name: 'Assinaturas & Recorrência',
    description: 'Controle de mensalidades, contratos e renovações automáticas.',
    category: 'finance'
  },
  {
    id: 'fin_expense_approvals',
    name: 'Aprovação de Despesas',
    description: 'Alçadas de aprovação para custos de anúncios e despesas operacionais.',
    category: 'finance'
  },
  {
    id: 'analytics_reports',
    name: 'Relatórios & Inteligência',
    description: 'Dashboards analíticos de conversão, produtividade e consumo de IA.',
    category: 'finance'
  },

  // 6. Integrações & Extensões
  {
    id: 'ext_integrations',
    name: 'Integrações Externas & Webhooks',
    description: 'Conectores com Stripe, Getfy, Meta Ads e endpoints de webhook.',
    category: 'integrations'
  },
  {
    id: 'ext_widgets',
    name: 'Widgets Embutíveis',
    description: 'Geração de widgets de chat para incorporação em sites externos.',
    category: 'integrations'
  }
];

export const MODULE_ALIASES: Record<string, string[]> = {
  inbox_conversations: ['inbox', 'conversations', 'inbox_conversations'],
  inbox_channels: ['channels', 'inbox_channels'],
  inbox_social_ops: ['social_ops', 'social_opportunities', 'inbox_social_ops'],
  inbox_social_health: ['social_health', 'inbox_social_health'],
  crm_leads: ['leads', 'crm_leads'],
  crm_deals: ['deals', 'crm_deals'],
  crm_pipelines: ['pipelines', 'crm_pipelines'],
  crm_contacts: ['contacts', 'crm_contacts'],
  crm_companies: ['companies', 'crm_companies'],
  crm_lead_sources: ['lead_sources', 'crm_lead_sources'],
  crm_tags: ['tags', 'crm_tags'],
  crm_custom_fields: ['custom_fields', 'crm_custom_fields'],
  ops_tasks: ['tasks', 'ops_tasks'],
  ops_schedules: ['schedules', 'ops_schedules'],
  ops_incidents: ['incidents', 'ops_incidents'],
  ai_agents: ['agents', 'ai_agents', 'ai_sdr', 'copilot'],
  ai_knowledge_base: ['knowledge_base', 'kb', 'ai_knowledge_base'],
  ai_automations: ['automations', 'automation', 'ai_automations'],
  ai_flow_builder: ['flow_builder', 'flows', 'ai_flow_builder'],
  ai_campaigns: ['campaigns', 'ai_campaigns'],
  ai_social_content: ['social_content', 'creatives_studio', 'media_gen', 'ai_social_content'],
  ai_lead_discovery: ['lead_discovery', 'ai_lead_discovery'],
  ai_growth_engine: ['growth_engine', 'growth', 'ai_growth_engine'],
  fin_invoicing: ['finance', 'invoicing', 'billings', 'fin_invoicing'],
  fin_subscriptions: ['subscriptions', 'fin_subscriptions'],
  fin_expense_approvals: ['expense_approvals', 'fin_expense_approvals'],
  analytics_reports: ['reports', 'analytics', 'analytics_reports', 'ai_usage'],
  ext_integrations: ['integrations', 'webhooks', 'ext_integrations'],
  ext_widgets: ['widgets', 'ext_widgets'],
};

export const PLAN_ENTITLEMENTS: Record<string, { label: string; modules: string[] }> = {
  starter: {
    label: 'Plano Starter',
    modules: [
      'inbox_conversations',
      'crm_leads',
      'crm_deals',
      'crm_pipelines',
      'crm_contacts',
      'crm_companies',
      'crm_tags',
      'ops_tasks',
    ],
  },
  flex: {
    label: 'Plano Flex (Modular)',
    modules: [
      'inbox_conversations',
      'crm_leads',
      'crm_deals',
      'crm_pipelines',
      'crm_contacts',
      'crm_companies',
      'crm_tags',
      'ops_tasks',
    ],
  },
  growth: {
    label: 'Plano Growth com IA',
    modules: [
      'inbox_conversations',
      'inbox_channels',
      'inbox_social_ops',
      'inbox_social_health',
      'crm_leads',
      'crm_deals',
      'crm_pipelines',
      'crm_contacts',
      'crm_companies',
      'crm_lead_sources',
      'crm_tags',
      'crm_custom_fields',
      'ops_tasks',
      'ops_schedules',
      'ops_incidents',
      'ai_agents',
      'ai_knowledge_base',
      'ai_automations',
      'ai_flow_builder',
      'ai_campaigns',
      'ai_social_content',
      'ai_lead_discovery',
      'ai_growth_engine',
      'fin_invoicing',
      'fin_subscriptions',
      'fin_expense_approvals',
      'analytics_reports',
      'ext_integrations',
      'ext_widgets',
    ],
  },
  enterprise: {
    label: 'Plano Enterprise AI',
    modules: SYSTEM_MODULES.map((m) => m.id),
  },
};

export const MODULE_PRESETS: Record<string, { label: string; modules: string[] }> = {
  all: {
    label: 'Acesso Total (Enterprise)',
    modules: SYSTEM_MODULES.map(m => m.id),
  },
  crm_standard: {
    label: 'CRM & Vendas Essencial',
    modules: [
      'crm_leads', 'crm_deals', 'crm_pipelines', 'crm_contacts', 'crm_companies',
      'crm_tags', 'ops_tasks', 'ops_schedules'
    ],
  },
  customer_service: {
    label: 'Atendimento & Omnichannel',
    modules: [
      'inbox_conversations', 'inbox_channels', 'inbox_social_ops', 'inbox_social_health',
      'crm_contacts', 'crm_tags', 'ops_tasks'
    ],
  },
  ai_growth: {
    label: 'Inteligência & Growth Autônomo',
    modules: [
      'ai_agents', 'ai_knowledge_base', 'ai_automations', 'ai_campaigns',
      'ai_social_content', 'ai_lead_discovery', 'ai_growth_engine'
    ],
  },
  instagram_ai_autopilot: {
    label: 'Instagram Feeds & Resposta IA',
    modules: [
      'ai_social_content',
      'ai_agents',
      'ai_knowledge_base',
      'inbox_social_ops',
      'inbox_channels',
      'inbox_social_health',
    ],
  },
};

/**
 * Verifica se um determinado módulo está contemplado pelo plano contratado e opcionais do tenant.
 * Regra: Administradores globais (role === 'admin') ou tenant master ('vanguarda')
 * têm acesso total irrevogável.
 * 
 * Tenants em plano Starter NÃO têm acesso a módulos de IA (ai_agents, ai_campaigns, etc.).
 * Tenants em plano Flex têm acesso aos módulos básicos + opcionais expressos em enabled_modules.
 * Tenants em plano Growth e Enterprise têm acesso amplo aos módulos de IA.
 */
export function hasModuleAccess(
  user: { role?: string; tenant_id?: string; plan?: string; enabled_modules?: string[] | null } | null | undefined,
  moduleId?: string
): boolean {
  if (!moduleId) return true; // Rotas sem restrição de módulo
  if (!user) return false;

  // 1. Administrador global ou tenant master tem acesso total irrevogável
  const role = String(user.role || '').toLowerCase();
  const tenantId = String(user.tenant_id || '').toLowerCase();
  if (role === 'admin' || role === 'superadmin' || tenantId === 'vanguarda') {
    return true;
  }

  // 2. Determinar a lista efetiva de módulos permitidos
  let allowedList: string[] = [];

  if (Array.isArray(user.enabled_modules) && user.enabled_modules.length > 0) {
    if (user.enabled_modules.includes('*')) {
      return true;
    }
    allowedList = user.enabled_modules;
  } else {
    // Se não há lista explícita, deriva estritamente do plano contratado
    const planKey = String(user.plan || 'starter').toLowerCase();
    const planConfig = PLAN_ENTITLEMENTS[planKey] || PLAN_ENTITLEMENTS.starter;
    allowedList = planConfig.modules;
  }

  // 3. Normalização de aliases cruzados para compatibilidade
  const targetAliases = MODULE_ALIASES[moduleId] || [moduleId];

  // O módulo é permitido se o próprio ID ou qualquer um dos seus aliases constar na lista
  return targetAliases.some(alias => allowedList.includes(alias)) || allowedList.includes(moduleId);
}

/**
 * Retorna o nome amigável do plano do tenant
 */
export function getPlanLabel(plan?: string): string {
  const key = String(plan || 'starter').toLowerCase();
  return PLAN_ENTITLEMENTS[key]?.label || 'Plano Starter';
}

